import { ref, computed } from 'vue';
import type {
  User,
  LoginCredentials,
  RegisterData,
  ForgotPasswordData,
  ResetPasswordData,
  VerifyEmailData,
} from '@/types/auth';
import { authApi } from '@/lib/api/auth';
import { useApiError } from './useApiError';

export interface AuthStateOptions {
  requireVerification?: boolean;
  persistToken?: (token: string) => void;
  persistRefreshToken?: (token: string) => void;
  getPersistedRefreshToken?: () => string | null;
  clearPersistedData?: () => void;
  getPersistedToken?: () => string | null;
  setPersistedUser?: (user: User) => void;
}

/**
 * Reusable authentication state management composable.
 * Updated for asab-identity backend (JSON login, email verification gate).
 */
export function useAuthState(options: AuthStateOptions = {}) {
  const {
    requireVerification = false,
    persistToken,
    persistRefreshToken,
    clearPersistedData,
    getPersistedToken,
    setPersistedUser,
  } = options;

  const { safeAuthCall } = useApiError();

  // Core auth state
  const user = ref<User | null>(null);
  const token = ref<string | null>(getPersistedToken?.() || null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const isInitialized = ref(false);

  // Computed getters
  const isAuthenticated = computed(() => !!token.value && !!user.value);
  const isVerified = computed(() => user.value?.is_verified ?? user.value?.email_verified ?? false);
  const isActive = computed(() => !!user.value && (user.value.is_active ?? true));
  const canAccess = computed(() => {
    if (!isAuthenticated.value) return false;
    if (requireVerification && !isVerified.value) return false;
    return isActive.value;
  });

  // Auth actions
  const login = async (credentials: LoginCredentials) => {
    try {
      isLoading.value = true;
      error.value = null;

      const response = await safeAuthCall(() => authApi.login(credentials), 'Login');

      if (response) {
        token.value = response.access_token;
        persistToken?.(response.access_token);

        // Persist refresh token for silent renewal
        if (response.refresh_token) {
          persistRefreshToken?.(response.refresh_token);
        }

        // asab-identity returns a user in the login response for an immediate
        // paint, but it is a public, pre-authorization endpoint and cannot carry
        // the gateway-resolved effective permissions. Always hydrate the full
        // profile from /me so permission-gated navigation works on first load.
        if (response.user) {
          user.value = response.user;
          setPersistedUser?.(response.user);
        }
        await getCurrentUser();

        return response;
      }

      throw new Error('Login failed');
    } catch (err: unknown) {
      const axiosErr = err as {
        isAxiosError?: boolean;
        response?: { status?: number; data?: { error?: string; detail?: string } };
      };

      // 403 = email not verified (asab-identity blocks login)
      if (axiosErr.response?.status === 403) {
        error.value =
          axiosErr.response.data?.error || 'Please verify your email address before logging in';
        // Re-throw the original AxiosError so callers can inspect detail
        // codes (LOGIN_PENDING_APPROVAL, LOGIN_ACCOUNT_BANNED, etc.).
        // Wrapping in `new Error(...)` loses the response shape and breaks
        // isAuthError downstream.
        throw err;
      }

      if (!error.value) {
        error.value = axiosErr.response?.data?.error || 'Login failed';
      }
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const register = async (userData: RegisterData) => {
    try {
      isLoading.value = true;
      error.value = null;

      const registeredUser = await authApi.register(userData);
      return registeredUser;
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      error.value = axiosErr.response?.data?.error || 'Registration failed';
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const forgotPassword = async (data: ForgotPasswordData) => {
    try {
      isLoading.value = true;
      error.value = null;

      await authApi.forgotPassword(data);
      return { success: true };
    } catch (err: unknown) {
      error.value = 'Failed to send reset email';
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const resetPassword = async (data: ResetPasswordData) => {
    try {
      isLoading.value = true;
      error.value = null;

      await authApi.resetPassword(data);
      return { success: true };
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      error.value = axiosErr.response?.data?.error || 'Password reset failed';
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const verifyEmail = async (verificationToken: string) => {
    try {
      isLoading.value = true;
      error.value = null;

      const data: VerifyEmailData = { token: verificationToken };
      const verifiedUser = await authApi.verifyEmail(data);

      // Update user if currently authenticated
      if (user.value && user.value.id === verifiedUser.id) {
        user.value = verifiedUser;
        setPersistedUser?.(verifiedUser);
      }

      return verifiedUser;
    } finally {
      isLoading.value = false;
    }
  };

  const requestVerifyToken = async (email: string) => {
    try {
      isLoading.value = true;
      error.value = null;

      await authApi.requestVerifyToken(email);
      return { success: true };
    } finally {
      isLoading.value = false;
    }
  };

  const getCurrentUser = async () => {
    try {
      if (!token.value) return null;

      isLoading.value = true;
      // The axios response interceptor handles 401 → refresh → retry automatically.
      // If it still fails after refresh, the error propagates here.
      const userData = await authApi.getCurrentUser();
      user.value = userData;
      setPersistedUser?.(userData);

      // Sync token from storage in case the interceptor refreshed it
      const currentToken = getPersistedToken?.();
      if (currentToken && currentToken !== token.value) {
        token.value = currentToken;
      }

      return userData;
    } catch (err: unknown) {
      // Token is truly invalid (refresh also failed), clear auth state
      await logout();
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const logout = async () => {
    try {
      if (token.value) {
        await authApi.logout();
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      user.value = null;
      token.value = null;
      error.value = null;
      isInitialized.value = false;
      clearPersistedData?.();
    }
  };

  const clearError = () => {
    error.value = null;
  };

  const initialize = async () => {
    if (isInitialized.value) return;

    try {
      const persistedToken = getPersistedToken?.();
      if (persistedToken) {
        token.value = persistedToken;
        await getCurrentUser();
      }
    } catch (err) {
      console.error('Failed to initialize auth state:', err);
      await logout();
    } finally {
      isInitialized.value = true;
    }
  };

  const refreshSession = async () => {
    if (!token.value) return false;

    try {
      await getCurrentUser();
      return true;
    } catch (err) {
      console.error('Session refresh failed:', err);
      await logout();
      return false;
    }
  };

  return {
    // State
    user,
    token,
    isLoading,
    error,
    isInitialized,

    // Computed getters
    isAuthenticated,
    isVerified,
    isActive,
    canAccess,

    // Actions
    login,
    register,
    forgotPassword,
    resetPassword,
    verifyEmail,
    requestVerifyToken,
    getCurrentUser,
    logout,
    clearError,
    initialize,
    refreshSession,
  };
}
